import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Activity, Target, Zap, Repeat, Download, ChevronDown, ChevronRight } from "lucide-react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import { useAuth } from "@/context/AuthContext";

function Counter({ value, suffix = "" }) {
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const controls = animate(mv, value, { duration: 1.4, ease: "easeOut" });
    mv.on("change", (v) => setDisplay(Math.floor(v)));
    return () => controls.stop();
  }, [value]);
  return <span className="font-display font-bold tabular-nums">{display.toLocaleString()}{suffix}</span>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [events, setEvents] = useState([]);
  const [weekly, setWeekly] = useState([]);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    (async () => {
      const [s, t, e, w] = await Promise.all([
        api.get("/dashboard/summary").then((r) => r.data),
        api.get("/dashboard/timeline").then((r) => r.data),
        api.get("/dashboard/events").then((r) => r.data),
        api.get("/dashboard/weekly").then((r) => r.data),
      ]);
      setSummary(s);
      setTimeline(t);
      setEvents(e.events);
      setWeekly(w.series);
    })();
  }, []);

  if (!summary) return <div className="p-8 text-[#555568]">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="user-dashboard">
      {/* How to read this dashboard */}
      <section className="rounded-xl border border-[#1A3A5C]/15 bg-gradient-to-br from-[#F8F9FC] to-white p-5" data-testid="dashboard-explainer">
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold">How to read this dashboard</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          <div className="flex items-start gap-3">
            <span className="h-7 w-7 rounded-md bg-[#1D9E75]/15 text-[#1D9E75] grid place-items-center text-xs font-bold font-mono">01</span>
            <div>
              <p className="text-sm font-semibold text-[#1A1A2E]">Top stats</p>
              <p className="text-xs text-[#555568]">Today's snapshot - focus score, FACE events, longest streak, and average refocus time.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="h-7 w-7 rounded-md bg-[#2C6BAC]/15 text-[#2C6BAC] grid place-items-center text-xs font-bold font-mono">02</span>
            <div>
              <p className="text-sm font-semibold text-[#1A1A2E]">Focus timeline</p>
              <p className="text-xs text-[#555568]">A continuous bar of your day. Green = priority work, amber = distraction, red = interruption. Hover any segment for length.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="h-7 w-7 rounded-md bg-[#BA7517]/15 text-[#BA7517] grid place-items-center text-xs font-bold font-mono">03</span>
            <div>
              <p className="text-sm font-semibold text-[#1A1A2E]">Weekly trend & events</p>
              <p className="text-xs text-[#555568]">Score (0–100) vs. number of distractions per day - paired so you can spot what drives focus down.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Top stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Target}
          label="Today's focus score"
          value={summary.focus_score}
          suffix=""
          tone="success"
          progress={summary.focus_score}
        />
        <StatCard
          icon={Activity}
          label="FACE events today"
          value={summary.face_events_total}
          breakdown={[`${summary.interruptions} interruptions`, `${summary.distractions} distractions`]}
        />
        <StatCard
          icon={Zap}
          label="Longest focus streak"
          value={summary.longest_streak_minutes}
          suffix=" min"
        />
        <StatCard
          icon={Repeat}
          label="Avg refocus this week"
          value={Math.floor(summary.avg_refocus_seconds / 60)}
          suffix=":" extra={String(summary.avg_refocus_seconds % 60).padStart(2, "0")}
        />
      </div>

      {/* Timeline */}
      <section className="rounded-xl border border-[#E8ECF2] bg-white p-6" data-testid="focus-timeline">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-bold text-[#1A1A2E]">Focus timeline · today</h2>
            <p className="text-xs text-[#555568] mt-1">Hour by hour. Green = priority work, amber = distraction, red = interruption.</p>
          </div>
        </div>
        <div className="relative h-14 rounded-md bg-[#F8F9FC] overflow-hidden border border-[#E8ECF2]">
          {timeline?.segments?.map((seg, i) => {
            const start = (seg.start_minute - timeline.day_start_minute) / (timeline.day_end_minute - timeline.day_start_minute);
            const width = (seg.end_minute - seg.start_minute) / (timeline.day_end_minute - timeline.day_start_minute);
            const colors = { focus: "#1D9E75", distraction: "#BA7517", interruption: "#E24B4A" };
            return (
              <div
                key={i}
                title={`${seg.label} · ${seg.end_minute - seg.start_minute} min`}
                className="absolute top-0 bottom-0 transition-opacity hover:opacity-80"
                style={{
                  left: `${start * 100}%`,
                  width: `${width * 100}%`,
                  background: colors[seg.kind],
                }}
              />
            );
          })}
        </div>
        {/* Hour axis */}
        <div className="relative h-5 mt-1" data-testid="timeline-hour-axis">
          {timeline && Array.from({ length: Math.floor((timeline.day_end_minute - timeline.day_start_minute) / 60) + 1 }).map((_, i) => {
            const hour = Math.floor(timeline.day_start_minute / 60) + i;
            const pct = (i * 60) / (timeline.day_end_minute - timeline.day_start_minute);
            return (
              <div
                key={i}
                className="absolute top-0 -translate-x-1/2 flex flex-col items-center"
                style={{ left: `${pct * 100}%` }}
              >
                <span className="block h-1.5 w-px bg-[#E8ECF2]" />
                <span className="text-[10px] font-mono text-[#555568]">{String(hour).padStart(2, "0")}:00</span>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex items-center gap-5 text-xs">
          <LegendDot color="#1D9E75" label="Focus" />
          <LegendDot color="#BA7517" label="Distraction" />
          <LegendDot color="#E24B4A" label="Interruption" />
        </div>
      </section>

      {/* Weekly trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 rounded-xl border border-[#E8ECF2] bg-white p-6">
          <h2 className="font-display text-xl font-bold text-[#1A1A2E] mb-4">Weekly trend</h2>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={weekly} margin={{ top: 5, right: 20, left: 10, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8ECF2" />
              <XAxis dataKey="day" stroke="#555568" fontSize={12} label={{ value: "Day", position: "insideBottom", offset: -6, fill: "#555568", fontSize: 11 }} />
              <YAxis yAxisId="left" stroke="#1A3A5C" fontSize={12} domain={[0, 100]} label={{ value: "Focus score (0–100)", angle: -90, position: "insideLeft", fill: "#1A3A5C", fontSize: 11, offset: 0 }} />
              <YAxis yAxisId="right" orientation="right" stroke="#BA7517" fontSize={12} label={{ value: "Distractions", angle: 90, position: "insideRight", fill: "#BA7517", fontSize: 11 }} />
              <Tooltip contentStyle={{ borderColor: "#E8ECF2", borderRadius: 8 }} />
              <Line yAxisId="left" name="Focus score" type="monotone" dataKey="focus_score" stroke="#1A3A5C" strokeWidth={2.5} dot={{ r: 4, fill: "#2C6BAC" }} />
              <Line yAxisId="right" name="Distractions" type="monotone" dataKey="distractions" stroke="#BA7517" strokeWidth={2} dot={{ r: 3, fill: "#BA7517" }} strokeDasharray="4 3" />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-3 flex items-center gap-5 text-xs">
            <LegendDot color="#1A3A5C" label="Focus score" />
            <LegendDot color="#BA7517" label="Distractions" />
          </div>
        </section>

        <section className="rounded-xl border border-[#E8ECF2] bg-white p-6">
          <h2 className="font-display text-xl font-bold text-[#1A1A2E] mb-4">Events by day</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={weekly} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8ECF2" />
              <XAxis dataKey="day" stroke="#555568" fontSize={12} label={{ value: "Day", position: "insideBottom", offset: -6, fill: "#555568", fontSize: 11 }} />
              <YAxis stroke="#555568" fontSize={12} label={{ value: "Event count", angle: -90, position: "insideLeft", fill: "#555568", fontSize: 11 }} />
              <Tooltip contentStyle={{ borderColor: "#E8ECF2", borderRadius: 8 }} />
              <Bar name="Distractions" dataKey="distractions" stackId="a" fill="#BA7517" />
              <Bar name="Interruptions" dataKey="interruptions" stackId="a" fill="#E24B4A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-3 flex items-center gap-5 text-xs">
            <LegendDot color="#BA7517" label="Distractions" />
            <LegendDot color="#E24B4A" label="Interruptions" />
          </div>
        </section>
      </div>

      {/* FACE event log */}
      <section className="rounded-xl border border-[#E8ECF2] bg-white" data-testid="face-event-log">
        <div className="flex items-center justify-between p-6 border-b border-[#E8ECF2]">
          <h2 className="font-display text-xl font-bold text-[#1A1A2E]">FACE event log</h2>
          <Button variant="outline" size="sm" data-testid="events-export">
            <Download size={14} className="mr-2" /> Export CSV
          </Button>
        </div>
        <ul className="divide-y divide-[#E8ECF2]">
          {events.slice(0, 12).map((e, idx) => (
            <li key={e.id || idx} className="px-6 py-4 hover:bg-[#F8F9FC]">
              <button
                onClick={() => setExpanded(expanded === idx ? null : idx)}
                className="w-full flex items-center justify-between text-left"
                data-testid={`event-${idx}`}
              >
                <div className="flex items-center gap-3">
                  <span className={`h-2 w-2 rounded-full ${e.face_type === "interruption" ? "bg-[#E24B4A]" : "bg-[#BA7517]"}`} />
                  <div>
                    <p className="text-sm text-[#1A1A2E]">
                      <span className="font-semibold capitalize">{e.face_type}</span>
                      <span className="text-[#555568]"> · {e.trigger_type}</span>
                    </p>
                    <p className="font-mono text-xs text-[#555568]">{formatDate(e.timestamp)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-[#555568]">
                    {Math.floor((e.face_duration_seconds || 0) / 60)}m {(e.face_duration_seconds || 0) % 60}s
                  </span>
                  {expanded === idx ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </div>
              </button>
              {expanded === idx && (
                <div className="mt-3 ml-5 text-xs text-[#555568] font-mono space-y-1">
                  <p>Trigger app: <span className="text-[#1A1A2E]">{e.last_priority_app || "-"}</span></p>
                  <p>Resolved: <span className="text-[#1A1A2E]">{e.resolved_at ? formatDate(e.resolved_at) : "Pending"}</span></p>
                  <p>Dismissed via: <span className="text-[#1A1A2E]">{e.dismissed_by || "-"}</span></p>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, suffix = "", extra = "", breakdown, tone, progress }) {
  return (
    <div className="rounded-xl border border-[#E8ECF2] bg-white p-6">
      <div className="flex items-start justify-between">
        <Icon size={18} className={tone === "success" ? "text-[#1D9E75]" : "text-[#2C6BAC]"} />
        {progress !== undefined && (
          <div className="relative h-9 w-9">
            <svg viewBox="0 0 36 36" className="h-9 w-9 -rotate-90">
              <circle cx="18" cy="18" r="14" stroke="#E8ECF2" strokeWidth="3" fill="none" />
              <circle cx="18" cy="18" r="14" stroke="#1D9E75" strokeWidth="3" fill="none"
                strokeDasharray={`${(progress / 100) * 88} 88`} strokeLinecap="round" />
            </svg>
          </div>
        )}
      </div>
      <p className="mt-4 text-3xl font-display font-bold text-[#1A1A2E]">
        <Counter value={value} suffix={suffix} />{extra}
      </p>
      <p className="text-xs text-[#555568] mt-1">{label}</p>
      {breakdown && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {breakdown.map((b, i) => (
            <span key={i} className="text-[10px] uppercase tracking-[0.15em] bg-[#F8F9FC] border border-[#E8ECF2] rounded-full px-2 py-0.5 font-mono">
              {b}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="flex items-center gap-1.5 text-[#555568]">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" });
  } catch {
    return iso;
  }
}
